import { useMemo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { DateTime } from "luxon";

import {
  useGetEvidenceAnalyticsQuery,
  useGetUserAnalyticsQuery,
} from "@/redux/service";
import { COMMONS_START_YEAR, SharingRights } from "@/shared/utils/constants";
import BaseBarChart from "@/components_v2/Charts/Bar/BaseBarChart";
import BasePieChart from "@/components_v2/Charts/Pie/BasePieChart";
import { HookFormBaseDropdown } from "@/components_v2/FormFields/ReactHookForm/fields";
import { yupResolver } from "@hookform/resolvers/yup";
import yup from "@/libraries/yup";
import ArchiveIcon from "@/assets/icons/archive.svg?react";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { AnalyticsProps } from "@/features/users/profile/types";

import "../styles.css";

const Analytics = ({ user }: AnalyticsProps) => {
  const { t } = useTranslation();

  const timeSchema = yup.object().shape({
    time: yup.string().typeError(t("form.labels.notValid")).nullable(),
  });

  const form = useForm({
    resolver: yupResolver(timeSchema),
    mode: "onChange",
    defaultValues: {
      time: "all_time",
    },
  });

  const formData = form.watch();

  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);

  const calculateDateRange = (
    timeValue: string | null | undefined,
  ): { startDate: string | undefined; endDate: string | undefined } => {
    if (!timeValue || timeValue === "all_time") {
      return { startDate: undefined, endDate: undefined };
    }

    if (/^\d{4}$/.test(timeValue)) {
      const year = parseInt(timeValue);
      return {
        startDate:
          DateTime.fromObject({ year, month: 1, day: 1 }).toISODate() ||
          undefined,
        endDate:
          DateTime.fromObject({ year, month: 12, day: 31 }).toISODate() ||
          undefined,
      };
    }

    return { startDate: undefined, endDate: undefined };
  };

  useEffect(() => {
    const dateRange = calculateDateRange(formData.time);
    setStartDate(dateRange.startDate ?? undefined);
    setEndDate(dateRange.endDate ?? undefined);
  }, [formData.time]);

  const { data: userAnalytics } = useGetUserAnalyticsQuery({
    userId: user.id,
    startDate,
    endDate,
  });
  const { data: evidenceAnalytics, isLoading: evidenceAnalyticsIsLoading } =
    useGetEvidenceAnalyticsQuery({
      userId: user.id,
      startDate,
      endDate,
    });

  const evidencePerSharingRightsChartData = useMemo(() => {
    if (!evidenceAnalytics) return { isEmpty: true, labels: [], datasets: [] };
    const filteredData = evidenceAnalytics.perSharingRights.filter(
      (item) => item.count > 0,
    );

    if (filteredData.length === 0) {
      return { isEmpty: true, labels: [], datasets: [] };
    }
    return {
      isEmpty: false,
      labels: filteredData.map(
        (item) => SharingRights.getItemByValue(item.label).label,
      ),
      datasets: [
        {
          data: filteredData.map((item) => item.count),
          backgroundColor: filteredData.map(
            (item) => SharingRights.getItemByValue(item.label).color,
          ),
          borderColor: "#FFFFFF",
          borderWidth: 6,
          hoverBorderWidth: 0,
          cutout: "50%",
          strokeColors: filteredData.map(
            (item) => SharingRights.getItemByValue(item.label).color,
          ),
          totalCount: evidenceAnalytics.totalCount,
        },
      ],
    };
  }, [evidenceAnalytics]);

  const evidencePerSectionChartData = useMemo(() => {
    if (!evidenceAnalytics) return { isEmpty: true, labels: [], datasets: [] };
    const maxCount = evidenceAnalytics.perSection.reduce(
      (max, item) => Math.max(max, item.count),
      0,
    );
    return {
      maxCount,
      total: evidenceAnalytics.perSection.reduce(
        (sum, item) => sum + item.count,
        0,
      ),
      labels: evidenceAnalytics.perSection.map((item) => item.label),
      datasets: [
        {
          data: evidenceAnalytics.perSection.map(() => Math.ceil(maxCount * 3)),
          backgroundColor: "#F3F3F7",
          hoverBackgroundColor: "#DFE4EC",
          stack: "stack1",
          order: 2,
        },
        {
          data: evidenceAnalytics.perSection.map((item) => item.count),
          backgroundColor: "#5B92E5",
          stack: "stack1",
          order: 1,
        },
      ],
    };
  }, [evidenceAnalytics]);

  const evidencePerTemplateChartData = useMemo(() => {
    if (!evidenceAnalytics) return { isEmpty: true, labels: [], datasets: [] };
    const maxCount = evidenceAnalytics.perTemplate.reduce(
      (max, item) => Math.max(max, item.count),
      0,
    );
    return {
      maxCount,
      total: evidenceAnalytics.perTemplate.reduce(
        (sum, item) => sum + item.count,
        0,
      ),
      labels: evidenceAnalytics.perTemplate.map((item) => item.label),
      datasets: [
        {
          data: evidenceAnalytics.perTemplate.map(() =>
            Math.ceil(maxCount * 3),
          ),
          backgroundColor: "#F3F3F7",
          hoverBackgroundColor: "#DFE4EC",
          stack: "stack1",
          order: 2,
        },
        {
          data: evidenceAnalytics.perTemplate.map((item) => item.count),
          backgroundColor: "#7A9E54",
          hoverBackgroundColor: "#486C37",
          stack: "stack1",
          order: 1,
        },
      ],
    };
  }, [evidenceAnalytics]);

  const evidencePerContributionChartData = useMemo(() => {
    if (!evidenceAnalytics) return { isEmpty: true, labels: [], datasets: [] };

    if (
      evidenceAnalytics.contributedToCount == null ||
      evidenceAnalytics.createdByCount == null
    ) {
      return { isEmpty: true, labels: [], datasets: [] };
    }

    const labels = [];
    const data = [];
    const colors = [];

    if (evidenceAnalytics.contributedToCount > 0) {
      labels.push(t("users.labels.contributedTo"));
      data.push(evidenceAnalytics.contributedToCount);
      colors.push("#5B92E5");
    }

    if (evidenceAnalytics.createdByCount > 0) {
      labels.push(t("users.labels.created"));
      data.push(evidenceAnalytics.createdByCount);
      colors.push("#ED901D");
    }

    if (data.length === 0) {
      return { isEmpty: true, labels: [], datasets: [] };
    }

    const totalCount = data.reduce((sum, count) => sum + count, 0);

    return {
      isEmpty: false,
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderColor: "#FFFFFF",
          borderWidth: 6,
          hoverBorderWidth: 0,
          cutout: "50%",
          strokeColors: colors,
          totalCount: totalCount,
        },
      ],
    };
  }, [evidenceAnalytics]);

  if (evidenceAnalyticsIsLoading) {
    return (
      <div className="analytics-loading-container">
        <BaseSpinner size="large" />
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const timeOptions = [
    { label: t("users.labels.allTime"), value: "all_time" },
    ...Array.from({ length: currentYear - COMMONS_START_YEAR + 1 }, (_, i) => {
      const year = currentYear - i;
      return { label: `${year}`, value: `${year}` };
    }),
  ];

  const getTimeLabel = (value: string | null | undefined) => {
    const option = timeOptions.find((option) => option.value === value);
    return option?.label || value || "";
  };

  const emptyStateBlock = (
    <div className="analytics-chart">
      <h3 className="body-m-medium">
        {t("users.labels.evidencePerSharingRights")}
      </h3>

      <div className="empty-state-content flex flex-col gap-100 full-height items-center justify-center">
        <BaseSvgRenderer component={ArchiveIcon} color="var(--neutral-700)" />

        <p className="body-m-medium">{t("users.labels.noDataAvailable")}</p>
      </div>
    </div>
  );

  return (
    <div className="page-layout">
      <div className="page-header">
        <h1 className="header-l-medium">
          {t("users.labels.dashboard", {
            filter: getTimeLabel(formData.time),
          })}
        </h1>

        <HookFormBaseDropdown
          name="time"
          options={timeOptions}
          control={form.control}
        />
      </div>

      <div className="analytics-container-user-overview-cards">
        <div className="analytics-container-overview-card evidence flex flex-col justify-center items-start full-width">
          <h2 className="header-xxl-medium">{evidenceAnalytics?.totalCount}</h2>
          <p className="body-m-medium">{t("users.labels.createdEvidence")}</p>
        </div>

        {userAnalytics && (
          <div className="analytics-container-overview-card disscussions flex flex-col justify-center items-start full-width">
            <h2 className="header-xxl-medium">
              {userAnalytics?.totalDiscussions}
            </h2>
            <p className="body-m-medium">{t("users.labels.discussions")}</p>
          </div>
        )}

        {userAnalytics && (
          <div className="analytics-container-overview-card comments flex flex-col justify-center items-start full-width">
            <h2 className="header-xxl-medium">
              {userAnalytics?.totalComments}
            </h2>
            <p className="body-m-medium">{t("users.labels.comments")}</p>
          </div>
        )}
      </div>

      <div className="analytics-container-graphs gap-300 full-width">
        {evidencePerSharingRightsChartData.isEmpty ? (
          emptyStateBlock
        ) : (
          <BasePieChart
            height={400}
            data={evidencePerSharingRightsChartData}
            title={t("users.labels.evidencePerSharingRights")}
            className="analytics-chart"
          />
        )}

        {evidencePerContributionChartData.isEmpty ? (
          emptyStateBlock
        ) : (
          <BasePieChart
            height={400}
            data={evidencePerContributionChartData}
            title={t("users.labels.evidencePerContribution")}
            className="analytics-chart"
          />
        )}

        <BaseBarChart
          height={600}
          data={evidencePerSectionChartData}
          title={t("users.labels.evidencePerSection")}
          className="analytics-chart"
        />

        <BaseBarChart
          height={600}
          data={evidencePerTemplateChartData}
          customOptions={{ indexAxis: "y" }}
          className="analytics-chart"
          title={t("users.labels.evidencePerEvidenceType")}
        />
      </div>
    </div>
  );
};

export default Analytics;
